import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { errorMessagesListFromResponse } from '../../../helpers/errors';
import { useAsyncValue, useDebouncedCallback, useOnMount } from '../../../hooks';
import TeamsModule from '../../../redux/teams';
import { getIssueCounts } from '../api';
import type { SnapshotFilters } from '../../../../shared/issuesV2/snapshots';
import type { GetIssueCountsResponse } from '../../../../shared/types/routes/issueCounts';
import type { FailedFetchResponse } from '../../../helpers/errors';

import useIssueOverview from './useIssueOverview';

/**
 * Time to wait for debouncing requests for data. This number was chosen arbitrarily on feeling.
 */
const fetchDebounceTimeMs = 200; // ms

const useIssueOverviewData = () => {
  const dispatch = useDispatch();
  const { filters, isProjectPage, setFilters } = useIssueOverview();
  const teamId = useSelector(TeamsModule.selectors.getTeamSelection);
  const { setPending, setReject, setResolve, ...value } = useAsyncValue<GetIssueCountsResponse>();

  // If we've loaded in team IDs from the URL, then we update the TeamSelectionMenu.
  useOnMount(() => {
    if (!isProjectPage) {
      if (filters.teamId) {
        dispatch(TeamsModule.actions.setTeamSelection(filters.teamId));
      }
    }
  });

  // Whenever the TeamSelectionMenu changes the team ID selection, we update the filters for the page.
  useEffect(() => {
    if (!isProjectPage && teamId !== filters.teamId) {
      setFilters({
        ...filters,
        teamId,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Adding filters and setFilters causes an infinite loop.
  }, [teamId, isProjectPage]);

  const debouncedFetchData = useDebouncedCallback((queryFilters: SnapshotFilters) => {
    async function fetchData() {
      try {
        setPending();
        const { data } = await getIssueCounts(queryFilters);
        if (!data) {
          throw new Error('No data found.');
        }
        setResolve(data);
      } catch (error) {
        setReject(errorMessagesListFromResponse(error as FailedFetchResponse));
      }
    }

    fetchData();
  }, fetchDebounceTimeMs);

  useEffect(() => debouncedFetchData(filters), [filters, debouncedFetchData]);

  return { value };
};

export default useIssueOverviewData;
